import { beginCell, type Cell } from '@ton/core';

/**
 * Buying a lottery ticket is just a plain TON transfer (>= 1 TON) to the
 * Pyramida contract:
 *   - empty body  -> the contract `receive()` handler registers the buyer,
 *     defaulting the referrer to the owner.
 *   - text comment containing a numeric referrer key -> the contract
 *     `receive(str: String)` handler parses the digits and uses that referrer.
 *
 * This mirrors the contract receivers in code_contracts/Pyramida.tact.
 */

/** Standard TEP-74 text comment cell (32 zero bits prefix + UTF-8 tail). */
export function buildCommentBody(comment: string): Cell {
  return beginCell().storeUint(0, 32).storeStringTail(comment).endCell();
}

/**
 * Body for a ticket purchase. When a referrer key is supplied it is sent as a
 * digits-only text comment; otherwise no body is attached (empty message).
 */
export function buildBuyTicketBody(referrerKey?: number | null): Cell | undefined {
  if (referrerKey != null && Number.isInteger(referrerKey) && referrerKey >= 0) {
    return buildCommentBody(String(referrerKey));
  }
  return undefined;
}

/** GiftTickets op-code (see code_contracts/pyramida/messages.tact). */
const OP_GIFT_TICKETS = 0x50524650;

/**
 * Body for gifting tickets to the owner of a numeric invite key. The full
 * attached amount buys the tickets for `key`'s wallet, while commissions and
 * registration follow the GIFTER's referral chain. `refKey` is the gifter's
 * own referrer key (first-touch, used only if the gifter is unregistered;
 * 0 = ecosystem owner, same as a buy without a comment).
 */
export function buildGiftTicketsBody(key: number, refKey?: number | null): Cell {
  const effectiveRefKey =
    refKey != null && Number.isInteger(refKey) && refKey >= 0 ? refKey : 0;
  return beginCell()
    .storeUint(OP_GIFT_TICKETS, 32)
    .storeUint(0, 64) // queryId (contract assigns its own pending-gift id)
    .storeUint(key, 64)
    .storeUint(effectiveRefKey, 64)
    .endCell();
}
